import { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { ValueListItemView } from '@messe/shared'
import { Button } from './ui/button.js'
import { TextField } from './ui/field.js'
import { Alert } from './ui/feedback.js'
import { Dialog } from './ui/dialog.js'
import { t } from '../i18n/index.js'
import { ApiError } from '../lib/api.js'
import { createValueListItem, updateValueListItem } from '../lib/value-lists.js'

interface Props {
  open: boolean
  listId: string
  isSupplierList: boolean
  /** Null when creating. */
  item: ValueListItemView | null
  onClose: () => void
  onSaved: (message: string) => void
}

interface FormState {
  label: string
  value: string
  sortOrder: string
  supplierNumber: string
  country: string
  contact: string
  note: string
}

const EMPTY: FormState = {
  label: '',
  value: '',
  sortOrder: '0',
  supplierNumber: '',
  country: '',
  contact: '',
  note: '',
}

function metadataString(item: ValueListItemView | null, key: string): string {
  const value = item?.metadata[key]
  return typeof value === 'string' ? value : ''
}

export function ValueListItemDialog({
  open,
  listId,
  isSupplierList,
  item,
  onClose,
  onSaved,
}: Props) {
  const strings = t()
  const queryClient = useQueryClient()
  const isEditing = item !== null

  const [values, setValues] = useState<FormState>(EMPTY)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const [formError, setFormError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setValues(
      item
        ? {
            label: item.label,
            value: item.value,
            sortOrder: String(item.sortOrder),
            supplierNumber: metadataString(item, 'supplierNumber'),
            country: metadataString(item, 'country'),
            contact: metadataString(item, 'contact'),
            note: metadataString(item, 'note'),
          }
        : EMPTY,
    )
    setFieldErrors({})
    setFormError(null)
  }, [open, item])

  function buildMetadata(): Record<string, unknown> | undefined {
    if (!isSupplierList) return undefined
    return {
      supplierNumber: values.supplierNumber.trim() || null,
      country: values.country.trim().toUpperCase() || null,
      contact: values.contact.trim() || null,
      note: values.note.trim() || null,
    }
  }

  const save = useMutation({
    mutationFn: async () => {
      const metadata = buildMetadata()
      const sortOrder = Number.parseInt(values.sortOrder, 10) || 0

      if (item) {
        return updateValueListItem(listId, item.id, {
          label: values.label.trim(),
          value: values.value.trim() || values.label.trim(),
          sortOrder,
          ...(metadata ? { metadata } : {}),
        })
      }

      return createValueListItem(listId, {
        label: values.label.trim(),
        ...(values.value.trim() ? { value: values.value.trim() } : {}),
        metadata: metadata ?? {},
        sortOrder,
      })
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['value-list-items'] })
      await queryClient.invalidateQueries({ queryKey: ['value-lists'] })
      onSaved(isEditing ? strings.valueLists.form.updated : strings.valueLists.form.created)
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        setFieldErrors(error.fieldErrors)
        setFormError(
          error.status === 409
            ? strings.valueLists.form.duplicate
            : (error.problem.detail ?? strings.errors.unexpected),
        )
        return
      }
      setFormError(strings.errors.unexpected)
    },
  })

  function validate(): boolean {
    const errors: Record<string, string> = {}
    if (values.label.trim() === '') errors['label'] = strings.errors.required
    // Two uppercase letters, matching the API's expectation, so the error shows
    // up here rather than as a server rejection.
    if (values.country.trim() !== '' && !/^[A-Za-z]{2}$/.test(values.country.trim())) {
      errors['country'] = 'ISO-Ländercode aus zwei Buchstaben, z. B. DE'
    }
    setFieldErrors(errors)
    return Object.keys(errors).length === 0
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={
        isEditing ? strings.valueLists.form.editHeading : strings.valueLists.form.createHeading
      }
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {strings.valueLists.actions.cancel}
          </Button>
          <Button
            disabled={save.isPending}
            onClick={() => {
              setFormError(null)
              if (validate()) save.mutate()
            }}
          >
            {save.isPending ? strings.valueLists.actions.saving : strings.valueLists.actions.save}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {formError && <Alert tone="danger">{formError}</Alert>}

        <TextField
          label={strings.valueLists.form.label}
          value={values.label}
          error={fieldErrors['label']}
          onChange={(event) => setValues((v) => ({ ...v, label: event.target.value }))}
        />

        <TextField
          label={strings.valueLists.form.value}
          hint={strings.valueLists.form.valueHint}
          value={values.value}
          error={fieldErrors['value']}
          onChange={(event) => setValues((v) => ({ ...v, value: event.target.value }))}
        />

        {isSupplierList && (
          <>
            <TextField
              label={strings.valueLists.columns.supplierNumber}
              value={values.supplierNumber}
              error={fieldErrors['metadata.supplierNumber']}
              onChange={(event) => setValues((v) => ({ ...v, supplierNumber: event.target.value }))}
            />
            <TextField
              label={strings.valueLists.columns.country}
              value={values.country}
              maxLength={2}
              error={fieldErrors['country'] ?? fieldErrors['metadata.country']}
              onChange={(event) => setValues((v) => ({ ...v, country: event.target.value }))}
            />
            <TextField
              label={strings.valueLists.columns.contact}
              value={values.contact}
              error={fieldErrors['metadata.contact']}
              onChange={(event) => setValues((v) => ({ ...v, contact: event.target.value }))}
            />
            <TextField
              label={strings.valueLists.columns.note}
              value={values.note}
              error={fieldErrors['metadata.note']}
              onChange={(event) => setValues((v) => ({ ...v, note: event.target.value }))}
            />
          </>
        )}

        <TextField
          label={strings.valueLists.form.sortOrder}
          type="number"
          value={values.sortOrder}
          onChange={(event) => setValues((v) => ({ ...v, sortOrder: event.target.value }))}
        />
      </div>
    </Dialog>
  )
}
